import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { toast } from "sonner";
import type { MDXEditorMethods } from "@mdxeditor/editor";
import { FollowupButtons, MessageList, MessageInput } from "@school-sales-ai/shared/components";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import type { FollowupOption, TestCapturedData, TestSessionSummary } from "@school-sales-ai/shared";
import { hasOnlyKnownGreetingPlaceholders } from "@school-sales-ai/shared";
import { TestSessionList } from "@/components/test-agent/TestSessionList";
import { useKbQuery, useKbMutation } from "@/hooks/useKnowledgeBase";
import { useKbDraft, KB_MIN_CHARS, KB_MAX_CHARS } from "@/hooks/useKbDraft";
import type { KbDraft } from "@/hooks/useKbDraft";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useLeadVisit } from "@/hooks/useLeadVisit";
import { VisitCard } from "@/components/VisitCard";
import { useUsers, useCurrentUserProfile } from "@/hooks/useUsers";
import { useTestAgent, TEST_QUICK_REPLIES } from "@/hooks/useTestAgent";
import type { ScoreDelta, TestTarget } from "@/hooks/useTestAgent";
import { useSuggestInitialOptionsMutation } from "@/hooks/useCatalog";
import {
  useDeleteLogoMutation,
  useSettingsMutation,
  useSettingsQuery,
  useUploadLogoMutation,
} from "@/hooks/useSettings";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { LeadOrdersSection } from "@/components/orders/LeadOrdersSection";
import { LeadQuotesSection } from "@/components/quotes/LeadQuotesSection";
import { PageHeader } from "@/components/PageHeader";
import { DocumentsManager } from "@/components/DocumentsManager";
import { InitialOptionsEditor } from "@/components/InitialOptionsEditor";
import { LeadAssignmentSettings } from "@/components/lead-assignment/LeadAssignmentSettings";
import { DeliveryZonesSettings } from "@/components/delivery-zones/DeliveryZonesSettings";
import { LocationSettings } from "@/components/LocationSettings";
import { MeetingLinkSettings } from "@/components/MeetingLinkSettings";
import { GreetingSettings } from "@/components/GreetingSettings";
import { ColorSettings } from "@/components/ColorSettings";
import { LogoSettings } from "@/components/LogoSettings";
import { ReengagementSettings } from "@/components/reengagement/ReengagementSettings";
import { brandByKey, type BrandDefinition, FEATURE_KEYS } from "@school-sales-ai/shared";
import { useClientBranding } from "@/hooks/useClientBranding";
import { formatCycle } from "@/lib/format-cycle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Lexical + Radix + CodeMirror ride along with this; nobody who never opens the
// knowledge base tab should download it.
const KbEditor = lazy(() =>
  import("@/components/KbEditor").then((m) => ({ default: m.KbEditor })),
);

const SCORE_CTA_THRESHOLD = 70;
const SCORE_ALERT_THRESHOLD = 85;

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

type DesktopTab = "kb" | "docs" | "settings" | "assignment" | "test";
type MobileTab = "kb" | "docs" | "settings" | "assignment" | "test";
type TestSubTab = "chat" | "debug";
// Within the single "Probar Agente" tab: the session list vs an open chat.
type TestView = "list" | "chat";

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function resolveAuthor(
  id: string,
  users: { id: string; name: string }[] | undefined,
  currentProfile: { id: string; name: string | null } | null,
): string {
  if (currentProfile?.id === id && currentProfile.name) return currentProfile.name;
  return users?.find((u) => u.id === id)?.name ?? `${id.slice(0, 8)}…`;
}

export function Agent(): React.JSX.Element {
  // KB state (existing)
  const { data: kbData, isLoading: kbLoading, isError: kbError } = useKbQuery();
  const kbMutation = useKbMutation();

  // Test-agent state: single "Probar Agente" tab shows a list of test sessions
  // (real isTest leads); opening/creating one drives the real chat flow.
  const [testView, setTestView] = useState<TestView>("list");
  const [testTarget, setTestTarget] = useState<TestTarget | null>(null);
  const testAgent = useTestAgent(testView === "chat" ? testTarget : null);

  const startNewTest = useCallback(() => {
    setTestTarget({ mode: "new", nonce: Date.now() });
    setTestView("chat");
  }, []);
  const openTest = useCallback((s: TestSessionSummary) => {
    setTestTarget({ mode: "resume", leadId: s.id, sessionRef: s.sessionRef });
    setTestView("chat");
  }, []);
  const backToTestList = useCallback(() => {
    setTestView("list");
    setTestTarget(null);
  }, []);

  const suggestMutation = useSuggestInitialOptionsMutation();

  // Presentation settings are their own resource now — they used to ride on
  // PUT /crm/catalog, which ran the catalog diff just to change a colour.
  const { data: settingsData } = useSettingsQuery();
  const settingsSave = useSettingsMutation();

  // The logo commits on its own (multipart), so it sits outside the dirty/save
  // cycle above. Gated on the plan feature — see `canBrand` below.
  const logoUpload = useUploadLogoMutation();
  const logoDelete = useDeleteLogoMutation();
  const { data: agentFeatures } = useClientFeatures();
  const canBrand = (agentFeatures ?? []).includes(FEATURE_KEYS.institutionalImage);
  // Uploading is wider than showing: a «Sitio web» tenant needs its logo on its
  // own site even without the widget avatar, which stays gated on `canBrand`.
  const canUploadLogo = canBrand || (agentFeatures ?? []).includes(FEATURE_KEYS.website);
  // Same source as the sidebar's mark. The test chat is the third consumer of
  // AgentAvatar (with the widget's bubble and typing indicator), and it must show
  // a prospect exactly what a prospect would see — including which mark backs a
  // client with no logo of its own.
  const { data: agentBranding } = useClientBranding();
  const brand = brandByKey(agentBranding?.brand);
  // Gates the delivery-zones setting in Ajustes: the `delivery` plan feature
  // («Envío a domicilio»), which the backend only grants together with `orders`.
  const deliveryEnabled = (agentFeatures ?? []).includes(FEATURE_KEYS.delivery);
  // Delivery zones are the shop's delivery price list: admin-only, like the
  // routes behind them (a member's request could only answer 403).
  const agentProfile = useCurrentUserProfile();
  const canEditZones =
    deliveryEnabled &&
    (agentProfile?.role === "admin" ||
      agentProfile?.role === "super_admin" ||
      agentProfile?.role === "account_manager");

  // «Mensajes de seguimiento» is sold per plan. Shown to every role — read-only
  // unless the server says this user may manage it.
  const canReengage = (agentFeatures ?? []).includes(FEATURE_KEYS.reengagement);

  // «Políticas de asignación de prospectos» is sold per plan: without the
  // `lead_assignment` feature the tab does not exist. The server reads the
  // stored policy as off for such a client, so hiding the tab hides nothing
  // that still has an effect.
  const canAssignLeads = (agentFeatures ?? []).includes(FEATURE_KEYS.leadAssignment);

  // Tab state
  const [tab, setTab] = useState<DesktopTab>("kb");
  const [mobileTab, setMobileTab] = useState<MobileTab>("kb");
  // A super-admin switching to a client without the feature while on that tab
  // would otherwise be left on a blank page.
  useEffect(() => {
    if (canAssignLeads) return;
    setTab((t) => (t === "assignment" ? "kb" : t));
    setMobileTab((t) => (t === "assignment" ? "kb" : t));
  }, [canAssignLeads]);
  const [testSubTab, setTestSubTab] = useState<TestSubTab>("chat");

  // KB editor state. The editor is uncontrolled once mounted, so the draft hook
  // tracks the text and `kbEditorRef` is how we push text back into it.
  const kbDraft = useKbDraft();
  const kbEditorRef = useRef<MDXEditorMethods>(null);
  const isDesktop = useIsDesktop();


  // Initial options state (Ajustes tab)
  const EMPTY_OPTIONS: FollowupOption[] = [
    { label: "", prompt: "" },
    { label: "", prompt: "" },
    { label: "", prompt: "" },
  ];
  const [initialOptions, setInitialOptions] = useState<FollowupOption[]>(EMPTY_OPTIONS);
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string>("");
  // Video-call link; only edited (and only sent) when the client's appointments
  // are online — a school never sees the field.
  const [meetingUrl, setMeetingUrl] = useState<string>("");
  // Widget primary color as hex; "" means "use the default" (persisted as null).
  const [primaryColor, setPrimaryColor] = useState<string>("");
  // Widget opening message; "" means "use the script's greeting" (persisted as null).
  const [greeting, setGreeting] = useState<string>("");

  // Hydrate once: once the user has touched a
  // field, only Discard or a successful Save may overwrite it.
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  useEffect(() => {
    if (!settingsData || settingsHydrated) return;
    setGoogleMapsUrl(settingsData.googleMapsUrl ?? "");
    setMeetingUrl(settingsData.meetingUrl ?? "");
    setPrimaryColor(settingsData.primaryColor ?? "");
    setGreeting(settingsData.greeting ?? "");
    const padded = [...(settingsData.initialOptions ?? [])];
    while (padded.length < 3) padded.push({ label: "", prompt: "" });
    setInitialOptions(padded.slice(0, 3) as FollowupOption[]);
    setSettingsHydrated(true);
  }, [settingsData, settingsHydrated]);

  // KB derived flags
  const kbSaved = kbData?.content ?? "";
  const { dirty: kbDirty, invalid: kbInvalid, seed: seedKb } = kbDraft;

  // The editor never reports the document it was mounted with — it only reports what the
  // user types (`KbEditor.contract.test.tsx`). So the saved knowledge base has to be pushed
  // into the hook as well, or the character count reads 0 over a full document and the
  // first keystroke is swallowed as the baseline instead of counting as an edit.
  const kbHydrated = useRef(false);
  useEffect(() => {
    if (kbHydrated.current || kbData === undefined) return;
    kbHydrated.current = true;
    seedKb(kbData?.content ?? "", kbData?.content ?? "");
  }, [kbData, seedKb]);

  // Config derived flags
  const savedInitialOptions: FollowupOption[] = settingsData?.initialOptions?.length
    ? ([...settingsData.initialOptions, { label: "", prompt: "" }, { label: "", prompt: "" }].slice(0, 3) as FollowupOption[])
    : EMPTY_OPTIONS;
  const savedGoogleMapsUrl = settingsData?.googleMapsUrl ?? "";
  const savedMeetingUrl = settingsData?.meetingUrl ?? "";
  const meetsOnline = settingsData?.visitMode === "online";
  const savedPrimaryColor = settingsData?.primaryColor ?? "";
  const savedGreeting = settingsData?.greeting ?? "";
  const initialOptionsDirty = JSON.stringify(initialOptions) !== JSON.stringify(savedInitialOptions);
  const googleMapsUrlDirty = googleMapsUrl.trim() !== savedGoogleMapsUrl;
  const googleMapsUrlInvalid = googleMapsUrl.trim().length > 0 && !isValidHttpUrl(googleMapsUrl.trim());
  const meetingUrlDirty = meetsOnline && meetingUrl.trim() !== savedMeetingUrl;
  const meetingUrlInvalid =
    meetsOnline && meetingUrl.trim().length > 0 && !isValidHttpUrl(meetingUrl.trim());
  const primaryColorDirty = primaryColor.trim().toLowerCase() !== savedPrimaryColor.toLowerCase();
  const primaryColorInvalid = primaryColor.trim().length > 0 && !HEX_COLOR_RE.test(primaryColor.trim());
  const greetingDirty = greeting.trim() !== savedGreeting;
  // Length is capped by the textarea itself; only the placeholders can go wrong.
  const greetingInvalid = !hasOnlyKnownGreetingPlaceholders(greeting);
  const settingsDirty =
    initialOptionsDirty ||
    googleMapsUrlDirty ||
    meetingUrlDirty ||
    primaryColorDirty ||
    greetingDirty;
  const settingsCannotSave =
    !settingsDirty ||
    googleMapsUrlInvalid ||
    meetingUrlInvalid ||
    primaryColorInvalid ||
    greetingInvalid ||
    settingsSave.isPending;

  const isDirty = kbDirty || settingsDirty;
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent): void {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Handlers — KB
  function handleKbDiscard(): void {
    // The editor is uncontrolled: it has to be told, not re-rendered.
    kbEditorRef.current?.setMarkdown(kbSaved);
    kbDraft.reset(kbSaved);
  }
  function handleKbSave(): void {
    kbMutation.mutate(
      { content: kbDraft.content },
      { onSuccess: () => kbDraft.markSaved() },
    );
  }

  // Handlers — Settings. Deliberately independent of the catalog save: these
  // fields carry no catalog data, so they must never run the diff or the
  // risky-deactivation confirmation.
  function handleSettingsDiscard(): void {
    setInitialOptions(savedInitialOptions);
    setGoogleMapsUrl(savedGoogleMapsUrl);
    setMeetingUrl(savedMeetingUrl);
    setPrimaryColor(savedPrimaryColor);
    setGreeting(savedGreeting);
  }
  function handleSettingsSave(): void {
    settingsSave.mutate({
      initialOptions: initialOptions.some((o) => o.label.trim())
        ? initialOptions.filter((o) => o.label.trim())
        : null,
      googleMapsUrl: googleMapsUrl.trim() ? googleMapsUrl.trim() : null,
      // Omitted unless the field is on screen: absent leaves the stored link alone.
      ...(meetsOnline ? { meetingUrl: meetingUrl.trim() ? meetingUrl.trim() : null } : {}),
      primaryColor: primaryColor.trim() ? primaryColor.trim().toLowerCase() : null,
      greeting: greeting.trim() ? greeting.trim() : null,
    });
  }
  function handleSuggestInitialOptions(): void {
    suggestMutation.mutate(undefined, {
      onSuccess: (opts) => {
        const padded = [...opts, { label: "", prompt: "" }, { label: "", prompt: "" }].slice(0, 3) as FollowupOption[];
        setInitialOptions(padded);
      },
    });
  }

  if (kbError) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <h1 className="font-display text-2xl font-bold text-ink-900">Agente</h1>
        <p className="text-sm text-red-500">
          Error al cargar la base de conocimiento. Intenta de nuevo.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Unsaved-changes blocker — portal, works on desktop and mobile */}
      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Salir sin guardar?</DialogTitle>
            <DialogDescription>
              Tienes cambios sin guardar. Si sales ahora, se perderán.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => blocker.reset?.()}
              className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => blocker.proceed?.()}
              className="rounded-lg bg-lime px-4 py-2 text-sm font-medium text-moss hover:bg-lime-dk"
            >
              Salir de todas formas
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivation confirmation */}

      {/* ── Mobile ─────────────────────────────────── */}
      <div className="md:hidden flex flex-col h-full">
        <PageHeader
          title="Agente"
          right={renderMobileHeaderRight({
            mobileTab,
            kbDirty,
            kbInvalid,
            kbMutationPending: kbMutation.isPending,
            handleKbDiscard,
            handleKbSave,
            testView,
            testReset: startNewTest,
          })}
        />

        {/* Main mobile tab strip */}
        <div className="bg-white border-b border-ink-100 flex flex-shrink-0">
          {(
            [
              ["kb", "Conocimiento"],
              ["docs", "Docs"],
              ["settings", "Ajustes"],
              ["assignment", "Asignación"],
              ["test", "Probar"],
            ] as const
          )
            .filter(([key]) => key !== "assignment" || canAssignLeads)
            .map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMobileTab(key)}
              className={`flex-1 py-2.5 text-[11px] border-b-2 transition-all cursor-pointer ${
                mobileTab === key
                  ? "font-semibold text-moss border-moss"
                  : "font-normal text-ink-500 border-transparent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* KB tab content */}
        {mobileTab === "kb" && (
          <KbTab
            kbData={kbData}
            isLoading={kbLoading}
            active={!isDesktop}
            savedContent={kbSaved}
            draft={kbDraft}
            editorRef={kbEditorRef}
            compact
          />
        )}


        {/* Documents tab content */}
        {mobileTab === "docs" && (
          <div className="flex-1 flex flex-col min-h-0">
            <DocumentsManager />
          </div>
        )}

        {/* Settings tab content */}
        {mobileTab === "settings" && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <GreetingSettings
                value={greeting}
                onChange={setGreeting}
                invalid={greetingInvalid}
                brand={brand}
              />
              <InitialOptionsEditor
                options={initialOptions}
                onChange={setInitialOptions}
                onSuggest={handleSuggestInitialOptions}
                isSuggesting={suggestMutation.isPending}
              />
              <LocationSettings
                value={googleMapsUrl}
                onChange={setGoogleMapsUrl}
                invalid={googleMapsUrlInvalid}
              />
              {meetsOnline && (
                <MeetingLinkSettings
                  value={meetingUrl}
                  onChange={setMeetingUrl}
                  invalid={meetingUrlInvalid}
                />
              )}
              <ColorSettings
                value={primaryColor}
                onChange={setPrimaryColor}
                invalid={primaryColorInvalid}
              />
              {canEditZones && <DeliveryZonesSettings />}
              {canReengage && <ReengagementSettings />}
              {canUploadLogo && (
                <LogoSettings
                  logoUrl={settingsData?.logoUrl ?? null}
                  brand={brand}
                  maxSizeMb={settingsData?.logoMaxSizeMb}
                  onUpload={(file) => logoUpload.mutate(file)}
                  onRemove={() => logoDelete.mutate()}
                  isUploading={logoUpload.isPending}
                  isRemoving={logoDelete.isPending}
                />
              )}
            </div>
            <div className="flex gap-2 border-t border-ink-100 bg-white px-4 py-3 flex-shrink-0">
              <button
                type="button"
                disabled={!settingsDirty || settingsSave.isPending}
                onClick={handleSettingsDiscard}
                className="flex-1 rounded-[10px] bg-ink-100 py-2.5 text-[13px] font-semibold text-ink-700 disabled:opacity-40"
              >
                Descartar
              </button>
              <button
                type="button"
                disabled={settingsCannotSave}
                onClick={handleSettingsSave}
                className="flex-[2] rounded-[10px] bg-lime py-2.5 text-[13px] font-semibold text-moss disabled:opacity-40"
              >
                {settingsSave.isPending ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        )}

        {/* Lead assignment policy — saves per switch, no footer */}
        {mobileTab === "assignment" && canAssignLeads && (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
            <LeadAssignmentSettings />
          </div>
        )}

        {/* Test agent tab content — list first, then the open chat */}
        {mobileTab === "test" &&
          (testView === "list" ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <TestSessionList onOpen={openTest} onNew={startNewTest} />
            </div>
          ) : (
            <MobileTestTab
              testSubTab={testSubTab}
              setTestSubTab={setTestSubTab}
              testAgent={testAgent}
              onBack={backToTestList}
              logoUrl={canBrand ? (settingsData?.logoUrl ?? null) : null}
              brand={brand}
            />
          ))}
      </div>

      {/* ── Desktop ────────────────────────────────── */}
      <div className="hidden md:flex flex-col h-full">
        <DesktopHeader
          tab={tab}
          kbData={kbData}
          kbDirty={kbDirty}
          kbInvalid={kbInvalid}
          kbMutationPending={kbMutation.isPending}
          settingsCannotSave={settingsCannotSave}
          settingsDirty={settingsDirty}
          settingsIsSaving={settingsSave.isPending}
          onSettingsDiscard={handleSettingsDiscard}
          onSettingsSave={handleSettingsSave}
          onKbDiscard={handleKbDiscard}
          onKbSave={handleKbSave}
        />

        <div className="flex border-b border-ink-100 px-6">
          {(
            [
              ["kb", "Base de Conocimiento"],
              ["docs", "Documentos generales"],
              ["settings", "Ajustes"],
              ["assignment", "Políticas de asignación de prospectos"],
              ["test", "Probar Agente"],
            ] as const
          )
            .filter(([key]) => key !== "assignment" || canAssignLeads)
            .map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`py-2.5 px-1 mr-6 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? "border-moss text-moss"
                  : "border-transparent text-ink-500 hover:text-ink-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "kb" && (
          <KbTab
            kbData={kbData}
            isLoading={kbLoading}
            active={isDesktop}
            savedContent={kbSaved}
            draft={kbDraft}
            editorRef={kbEditorRef}
          />
        )}
        {tab === "docs" && <DocumentsManager />}
        {tab === "settings" && (
          <DesktopSettingsTab
            options={initialOptions}
            onChange={setInitialOptions}
            onSuggest={handleSuggestInitialOptions}
            isSuggesting={suggestMutation.isPending}
            googleMapsUrl={googleMapsUrl}
            onGoogleMapsUrlChange={setGoogleMapsUrl}
            googleMapsUrlInvalid={googleMapsUrlInvalid}
            meetsOnline={meetsOnline}
            meetingUrl={meetingUrl}
            onMeetingUrlChange={setMeetingUrl}
            meetingUrlInvalid={meetingUrlInvalid}
            primaryColor={primaryColor}
            onPrimaryColorChange={setPrimaryColor}
            primaryColorInvalid={primaryColorInvalid}
            greeting={greeting}
            onGreetingChange={setGreeting}
            greetingInvalid={greetingInvalid}
            canEditZones={canEditZones}
            canReengage={canReengage}
            canUploadLogo={canUploadLogo}
            logoUrl={settingsData?.logoUrl ?? null}
            logoMaxSizeMb={settingsData?.logoMaxSizeMb}
            brand={brand}
            onLogoUpload={(file) => logoUpload.mutate(file)}
            onLogoRemove={() => logoDelete.mutate()}
            isLogoUploading={logoUpload.isPending}
            isLogoRemoving={logoDelete.isPending}
          />
        )}
        {tab === "assignment" && canAssignLeads && (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <div className="max-w-2xl">
              <LeadAssignmentSettings />
            </div>
          </div>
        )}
        {tab === "test" &&
          (testView === "list" ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <TestSessionList onOpen={openTest} onNew={startNewTest} />
            </div>
          ) : (
            <TestAgentTab
              {...testAgent}
              onReset={startNewTest}
              onBack={backToTestList}
              logoUrl={canBrand ? (settingsData?.logoUrl ?? null) : null}
              brand={brand}
            />
          ))}
      </div>
    </>
  );
}

// ─── Mobile header right slot ────────────────────────────────────────────────

function renderMobileHeaderRight({
  mobileTab,
  kbDirty,
  kbInvalid,
  kbMutationPending,
  handleKbDiscard,
  handleKbSave,
  testView,
  testReset,
}: {
  mobileTab: MobileTab;
  kbDirty: boolean;
  kbInvalid: boolean;
  kbMutationPending: boolean;
  handleKbDiscard: () => void;
  handleKbSave: () => void;
  testView: TestView;
  testReset: () => void;
}): React.ReactNode {
  // Only in the open-chat view — the list has its own "Nueva prueba" button.
  if (mobileTab === "test") {
    if (testView !== "chat") return null;
    return (
      <button
        type="button"
        onClick={testReset}
        className="px-3 py-1.5 bg-ink-100 rounded-[10px] text-[12px] font-semibold text-ink-700"
      >
        Nueva prueba
      </button>
    );
  }

  if (mobileTab === "kb") {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!kbDirty || kbMutationPending}
          onClick={handleKbDiscard}
          className="rounded-[10px] border border-ink-200 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-700 disabled:opacity-40"
        >
          Descartar
        </button>
        <button
          type="button"
          disabled={!kbDirty || kbInvalid || kbMutationPending}
          onClick={handleKbSave}
          className="rounded-[10px] bg-lime px-3 py-1.5 text-[12px] font-semibold text-moss disabled:opacity-40"
        >
          {kbMutationPending ? "…" : "Guardar"}
        </button>
      </div>
    );
  }

  // Config tab — actions live in the sticky footer.
  return null;
}

// ─── KB tab (shared by both layouts) ─────────────────────────────────────────

type KbTabProps = {
  kbData:
    | { version: number; createdAt: string; updatedBy: string }
    | null
    | undefined;
  isLoading: boolean;
  /**
   * Whether this layout is the visible one. Both layouts are in the DOM at all
   * times, and two live editors would fight over the same draft, so only the
   * visible one mounts a real editor.
   */
  active: boolean;
  savedContent: string;
  draft: KbDraft;
  editorRef: React.RefObject<MDXEditorMethods>;
  compact?: boolean;
};

function KbTab({
  kbData,
  isLoading,
  active,
  savedContent,
  draft,
  editorRef,
  compact = false,
}: KbTabProps): React.JSX.Element {
  const { data: users } = useUsers();
  const currentProfile = useCurrentUserProfile();
  const { content, hasEditedOnce, tooShort, tooLong, problem } = draft;

  if (!active) return <div className="flex min-h-0 flex-1 flex-col" />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {kbData && compact && (
        <div className="flex-shrink-0 border-b border-ink-100 bg-ink-050 px-4 py-2">
          <span className="text-[11px] text-ink-400">
            Versión {kbData.version} · {formatDate(kbData.createdAt)} por{" "}
            {resolveAuthor(kbData.updatedBy, users, currentProfile)}
          </span>
        </div>
      )}

      {isLoading || !kbData ? (
        <div className="flex-1 space-y-3 p-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-4 animate-pulse rounded bg-ink-100"
              style={{ width: `${60 + i * 10}%` }}
            />
          ))}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
        <Suspense
          fallback={
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-4 animate-pulse rounded bg-ink-100"
                  style={{ width: `${70 - i * 10}%` }}
                />
              ))}
            </div>
          }
        >
          <KbEditor
            ref={editorRef}
            markdown={draft.content || savedContent}
            diffMarkdown={savedContent}
            onChange={draft.onEditorChange}
            placeholder="Escribe aquí la información que el agente debe conocer…"
            onError={(message) =>
              toast.error(
                "No se pudo abrir el editor visual; se muestra el texto sin formato.",
                { description: message },
              )
            }
          />
        </Suspense>
        </div>
      )}

      <div className="flex flex-shrink-0 items-center justify-between border-t border-ink-100 bg-ink-050 px-4 py-2">
        {problem ? (
          <p className={`text-red-500 ${compact ? "text-[11px]" : "text-xs"}`}>
            {problem}
          </p>
        ) : hasEditedOnce && tooShort ? (
          <p className={`text-red-500 ${compact ? "text-[11px]" : "text-xs"}`}>
            Mínimo {KB_MIN_CHARS} caracteres requeridos.
          </p>
        ) : (
          <span />
        )}
        <p
          className={`${compact ? "text-[11px]" : "text-xs"} ${
            tooLong ? "font-medium text-red-500" : "text-ink-400"
          }`}
        >
          {content.length.toLocaleString("es-MX")} /{" "}
          {KB_MAX_CHARS.toLocaleString("es-MX")}
        </p>
      </div>
    </div>
  );
}

// ─── Mobile Config tab ───────────────────────────────────────────────────────


// ─── Mobile Test tab ─────────────────────────────────────────────────────────

type MobileTestTabProps = {
  testSubTab: TestSubTab;
  setTestSubTab: (t: TestSubTab) => void;
  testAgent: ReturnType<typeof useTestAgent>;
  onBack: () => void;
  /** Plan-gated school logo, so the preview matches what a prospect sees. */
  logoUrl: string | null;
  /** The brand whose mark backs a missing logo — from /crm/branding, like the sidebar. */
  brand: BrandDefinition;
};

function MobileTestTab({
  testSubTab,
  setTestSubTab,
  testAgent,
  onBack,
  logoUrl,
  brand,
}: MobileTestTabProps): React.JSX.Element {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center border-b border-ink-100 bg-white px-4 py-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-600"
        >
          ← Volver a la lista
        </button>
      </div>
      <div className="bg-ink-050 border-b border-ink-100 flex flex-shrink-0">
        {(["chat", "debug"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTestSubTab(t)}
            className={`flex-1 py-2 text-[11px] border-b-2 transition-all cursor-pointer ${
              testSubTab === t
                ? "font-semibold text-moss border-moss bg-white"
                : "font-normal text-ink-500 border-transparent"
            }`}
          >
            {t === "chat" ? "Chat" : "Debug"}
          </button>
        ))}
      </div>

      {testSubTab === "chat" && (
        <div className="flex-1 flex flex-col min-h-0 bg-ink-050">
          <MessageList
            messages={testAgent.messages}
            isTyping={testAgent.isTyping}
            showInitialReplies={testAgent.showInitialReplies}
            quickReplies={testAgent.initialOptions ?? TEST_QUICK_REPLIES}
            logoUrl={logoUrl}
            brand={brand}
            onQuickReply={testAgent.send}
            ctaSlot={testAgent.showWhatsappCta ? <TestWhatsAppCTA /> : undefined}
          />
          <FollowupButtons options={testAgent.followupOptions} onSelect={testAgent.send} />
          <MessageInput onSend={testAgent.send} disabled={testAgent.isTyping} />
        </div>
      )}

      {testSubTab === "debug" && (
        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-300">
          <div>
            <p className="text-[11px] font-medium text-ink-500 mb-1">Sesión</p>
            <span className="font-mono text-[13px] text-ink-800">
              {testAgent.sessionRef ?? "—"}
            </span>
          </div>
          <div>
            <p className="text-[11px] font-medium text-ink-500 mb-1">Score actual</p>
            <ScoreBadge score={testAgent.score} size="md" />
          </div>
          <div>
            <p className="text-[11px] font-medium text-ink-500 mb-2">Umbrales</p>
            <ScoreThresholdBar score={testAgent.score} />
          </div>
          <CapturedDataSection data={testAgent.capturedData} />
          <VisitSection leadId={testAgent.leadId} />
          <CloseSection leadId={testAgent.leadId} />
          <div>
            <p className="text-[11px] font-medium text-ink-500 mb-2">
              Historial de score
            </p>
            {testAgent.scoreHistory.length === 0 ? (
              <p className="text-[12px] text-ink-400">Sin interacciones aún.</p>
            ) : (
              <ul className="space-y-1">
                {testAgent.scoreHistory.map((entry) => (
                  <ScoreDeltaRow key={entry.messageId} entry={entry} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Desktop header ──────────────────────────────────────────────────────────

type DesktopHeaderProps = {
  tab: DesktopTab;
  kbData:
    | { version: number; createdAt: string; updatedBy: string }
    | null
    | undefined;
  kbDirty: boolean;
  kbInvalid: boolean;
  kbMutationPending: boolean;
  settingsCannotSave: boolean;
  settingsDirty: boolean;
  settingsIsSaving: boolean;
  onSettingsDiscard: () => void;
  onSettingsSave: () => void;
  onKbDiscard: () => void;
  onKbSave: () => void;
};

function DesktopHeader({
  tab,
  kbData,
  kbDirty,
  kbInvalid,
  kbMutationPending,
  settingsCannotSave,
  settingsDirty,
  settingsIsSaving,
  onSettingsDiscard,
  onSettingsSave,
  onKbDiscard,
  onKbSave,
}: DesktopHeaderProps): React.JSX.Element {
  const { data: users } = useUsers();
  const currentProfile = useCurrentUserProfile();
  let subtitle: React.ReactNode = null;
  if (tab === "kb" && kbData) {
    subtitle = (
      <p className="mt-0.5 text-xs text-ink-400">
        Versión {kbData.version} · {formatDate(kbData.createdAt)} por{" "}
        {resolveAuthor(kbData.updatedBy, users, currentProfile)}
      </p>
    );
  } else if (tab === "docs") {
    subtitle = (
      <p className="mt-0.5 text-xs text-ink-400">
        Materiales generales (PDF, imagen o video) que el agente puede enviar por WhatsApp. Los de cada programa están en Catálogo.
      </p>
    );
  } else if (tab === "settings") {
    subtitle = (
      <p className="mt-0.5 text-xs text-ink-400">
        Comportamiento del agente: opciones iniciales, nombre, tono y más.
      </p>
    );
  } else if (tab === "assignment") {
    subtitle = (
      <p className="mt-0.5 text-xs text-ink-400">
        Quién atiende a cada prospecto y quién puede verlo. Cada cambio se guarda al
        momento.
      </p>
    );
  }

  return (
    <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-900">Agente</h1>
        {subtitle}
      </div>

      {tab === "kb" && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!kbDirty}
            onClick={onKbDiscard}
            className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Descartar
          </button>
          <button
            type="button"
            disabled={!kbDirty || kbInvalid || kbMutationPending}
            onClick={onKbSave}
            className="rounded-lg bg-lime px-4 py-2 text-sm font-medium text-moss hover:bg-lime-dk disabled:cursor-not-allowed disabled:opacity-40"
          >
            {kbMutationPending ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      )}

      {tab === "settings" && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!settingsDirty || settingsIsSaving}
            onClick={onSettingsDiscard}
            className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Descartar
          </button>
          <button
            type="button"
            disabled={settingsCannotSave}
            onClick={onSettingsSave}
            className="rounded-lg bg-lime px-4 py-2 text-sm font-medium text-moss hover:bg-lime-dk disabled:cursor-not-allowed disabled:opacity-40"
          >
            {settingsIsSaving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Desktop Config tab ──────────────────────────────────────────────────────


// ─── Desktop Settings tab ────────────────────────────────────────────────────

type DesktopSettingsTabProps = {
  options: FollowupOption[];
  onChange: (options: FollowupOption[]) => void;
  onSuggest: () => void;
  isSuggesting: boolean;
  googleMapsUrl: string;
  onGoogleMapsUrlChange: (value: string) => void;
  googleMapsUrlInvalid: boolean;
  /** The client's appointments are online: show the video-call link field. */
  meetsOnline: boolean;
  meetingUrl: string;
  onMeetingUrlChange: (value: string) => void;
  meetingUrlInvalid: boolean;
  primaryColor: string;
  onPrimaryColorChange: (value: string) => void;
  primaryColorInvalid: boolean;
  greeting: string;
  onGreetingChange: (value: string) => void;
  greetingInvalid: boolean;
  /** Admin of a shop with orders: may draw delivery zones — and their fees. */
  canEditZones: boolean;
  /** The plan grants «Mensajes de seguimiento» (the card itself checks the role). */
  canReengage: boolean;
  // Logo props are separate from the others: it saves on its own (multipart),
  // so it carries no dirty/invalid state for the page's save button to read.
  canUploadLogo: boolean;
  logoUrl: string | null;
  logoMaxSizeMb: number | undefined;
  /** The client's brand — whose mark stands in for a missing logo. */
  brand: BrandDefinition;
  onLogoUpload: (file: File) => void;
  onLogoRemove: () => void;
  isLogoUploading: boolean;
  isLogoRemoving: boolean;
};

function DesktopSettingsTab({
  options,
  onChange,
  onSuggest,
  isSuggesting,
  googleMapsUrl,
  onGoogleMapsUrlChange,
  googleMapsUrlInvalid,
  meetsOnline,
  meetingUrl,
  onMeetingUrlChange,
  meetingUrlInvalid,
  primaryColor,
  onPrimaryColorChange,
  primaryColorInvalid,
  greeting,
  onGreetingChange,
  greetingInvalid,
  canEditZones,
  canReengage,
  canUploadLogo,
  logoUrl,
  logoMaxSizeMb,
  brand,
  onLogoUpload,
  onLogoRemove,
  isLogoUploading,
  isLogoRemoving,
}: DesktopSettingsTabProps): React.JSX.Element {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl">
        <GreetingSettings
          value={greeting}
          onChange={onGreetingChange}
          invalid={greetingInvalid}
          brand={brand}
        />
        <InitialOptionsEditor
          options={options}
          onChange={onChange}
          onSuggest={onSuggest}
          isSuggesting={isSuggesting}
        />
        <LocationSettings
          value={googleMapsUrl}
          onChange={onGoogleMapsUrlChange}
          invalid={googleMapsUrlInvalid}
        />
        {meetsOnline && (
          <MeetingLinkSettings
            value={meetingUrl}
            onChange={onMeetingUrlChange}
            invalid={meetingUrlInvalid}
          />
        )}
        <ColorSettings
          value={primaryColor}
          onChange={onPrimaryColorChange}
          invalid={primaryColorInvalid}
        />
        {canEditZones && <DeliveryZonesSettings />}
        {canReengage && <ReengagementSettings />}
        {canUploadLogo && (
          <LogoSettings
            logoUrl={logoUrl}
            brand={brand}
            maxSizeMb={logoMaxSizeMb}
            onUpload={onLogoUpload}
            onRemove={onLogoRemove}
            isUploading={isLogoUploading}
            isRemoving={isLogoRemoving}
          />
        )}
      </div>
    </div>
  );
}

// ─── Test agent tab (desktop) ────────────────────────────────────────────────

type TestAgentTabProps = ReturnType<typeof useTestAgent> & {
  onReset: () => void;
  onBack: () => void;
  /** Plan-gated school logo, so the preview matches what a prospect sees. */
  logoUrl: string | null;
  /** The brand whose mark backs a missing logo — from /crm/branding, like the sidebar. */
  brand: BrandDefinition;
};

function TestAgentTab({
  messages,
  isTyping,
  showInitialReplies,
  initialOptions,
  followupOptions,
  score,
  scoreHistory,
  sessionRef,
  leadId,
  showWhatsappCta,
  capturedData,
  send,
  onReset,
  onBack,
  logoUrl,
  brand,
}: TestAgentTabProps): React.JSX.Element {
  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex w-3/5 flex-col border-r border-ink-100 bg-ink-050">
        <div className="flex items-center border-b border-ink-100 bg-white px-4 py-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-600 hover:text-moss transition-colors"
          >
            ← Volver a la lista
          </button>
        </div>
        <MessageList
          messages={messages}
          isTyping={isTyping}
          showInitialReplies={showInitialReplies}
          quickReplies={initialOptions ?? TEST_QUICK_REPLIES}
          logoUrl={logoUrl}
          brand={brand}
          onQuickReply={send}
          ctaSlot={showWhatsappCta ? <TestWhatsAppCTA /> : undefined}
        />
        <FollowupButtons options={followupOptions} onSelect={send} />
        <MessageInput onSend={send} disabled={isTyping} />
      </div>

      <div className="flex w-2/5 flex-col gap-4 overflow-y-auto p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink-700">Debug</h2>
          <button
            type="button"
            onClick={onReset}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-050 transition-colors"
          >
            Nueva prueba
          </button>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-ink-500">Sesión</p>
          <span className="font-mono text-sm text-ink-800">
            {sessionRef ?? "—"}
          </span>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-ink-500">Score actual</p>
          <ScoreBadge score={score} size="md" />
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-ink-500">Umbrales</p>
          <ScoreThresholdBar score={score} />
        </div>

        <CapturedDataSection data={capturedData} />

        <VisitSection leadId={leadId} />

        <CloseSection leadId={leadId} />

        <div className="flex-1">
          <p className="mb-2 text-xs font-medium text-ink-500">
            Historial de score
          </p>
          {scoreHistory.length === 0 ? (
            <p className="text-xs text-ink-400">Sin interacciones aún.</p>
          ) : (
            <ul className="space-y-1">
              {scoreHistory.map((entry) => (
                <ScoreDeltaRow key={entry.messageId} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared mini bits ────────────────────────────────────────────────────────

// Shows the lead's visit once one exists; renders nothing otherwise.
// The query lives under ["lead", leadId, "visit"], so the per-turn lead
// invalidation in useTestAgent refreshes it right after the agent books.
function VisitSection({ leadId }: { leadId: string | null }): React.JSX.Element | null {
  const { data } = useLeadVisit(leadId ?? "");
  if (!data?.visit) return null;
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-ink-500">Visita agendada</p>
      <VisitCard visit={data.visit} />
    </div>
  );
}

// What the test conversation produced as a close: the cart and its orders, or
// the quote requests — the same cards the lead detail shows, each linking to its
// detail page. Only the close this client's plan has; both render nothing until
// there is something to show, and useTestAgent refreshes them after every turn.
function CloseSection({ leadId }: { leadId: string | null }): React.JSX.Element | null {
  const { data: features } = useClientFeatures();
  if (!leadId) return null;
  const canOrders = (features ?? []).includes(FEATURE_KEYS.orders);
  const canQuotes = (features ?? []).includes(FEATURE_KEYS.quotes);
  if (!canOrders && !canQuotes) return null;
  return (
    <>
      {canOrders && <LeadOrdersSection leadId={leadId} />}
      {canQuotes && <LeadQuotesSection leadId={leadId} />}
    </>
  );
}

function CapturedDataSection({ data }: { data: TestCapturedData | null }): React.JSX.Element {
  // `programs` already excludes anything shown on a person's row — mapCaptured
  // does that, so the rule is not restated here.
  const programs = data?.programs ?? [];
  const candidates = data?.candidates ?? [];
  const hasCandidates = candidates.length > 0;

  const rows: Array<{ label: string; value: string | null }> = [
    { label: "Nombre", value: data?.name ?? null },
    { label: "Teléfono", value: data?.phone ?? null },
    { label: "Email", value: data?.email ?? null },
    // One row per person, each carrying their own grade/age and program. Omitted
    // entirely when there are none: a prospect enrolling themselves correctly
    // has no candidates, and an "Alumno —" placeholder would read as a miss.
    ...candidates.map((c) => ({
      label: "Alumno",
      value: [c.name, c.detail, c.programName].filter((part) => !!part).join(" · "),
    })),
    // One row per program: a prospect comparing two is the normal case, and a
    // single row showed only the primary — so this panel claimed the agent had
    // captured one program when it had captured both. The primary is marked
    // only when there is something to distinguish it from.
    //
    // With candidates present the empty placeholder is dropped too: the programs
    // were captured, they are just displayed on their owner's row above.
    ...(programs.length === 0
      ? hasCandidates
        ? []
        : [{ label: "Programa", value: null }]
      : programs.map((p) => ({
          label: "Programa",
          value:
            !hasCandidates && programs.length > 1 && p.isPrimary
              ? `${p.name} (principal)`
              : p.name,
        }))),
    { label: "Ciclo", value: formatCycle(data?.cycleKey ?? null, data?.cycleLabel ?? null) },
  ];
  // Custom capture fields only. Their keys are the admin-defined `name`s from the
  // catalog's CAMPOS A RECABAR section, so they double as the display label.
  for (const [k, v] of Object.entries(data?.fields ?? {})) {
    rows.push({ label: k, value: typeof v === "string" ? v : String(v) });
  }
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-ink-500">Datos capturados</p>
      <dl className="space-y-1">
        {rows.map(({ label, value }, i) => (
          <div key={`${label}-${i}`} className="flex gap-2 text-xs">
            <dt className="shrink-0 text-ink-400">{label}</dt>
            <dd className={value ? "font-medium text-ink-800" : "text-ink-300"}>
              {value ?? "—"}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TestWhatsAppCTA(): React.JSX.Element {
  return (
    <div className="mx-0 my-2 p-3 rounded-xl border border-[oklch(0.85_0.06_145)] bg-[oklch(0.97_0.03_145)]">
      <p className="text-[12.5px] font-semibold text-ink-900 mb-0.5">
        ¡CTA de WhatsApp activada! 🎉
      </p>
      <p className="text-[12px] text-ink-600">
        Vera detectó una señal de cierre. En producción aparecería el botón de WhatsApp.
      </p>
    </div>
  );
}

function ScoreThresholdBar({ score }: { score: number }): React.JSX.Element {
  return (
    <>
      <div className="relative">
        <div className="h-3 overflow-hidden rounded-full bg-ink-100">
          <div
            className="h-full bg-lime transition-[width] duration-500 ease-out"
            style={{ width: `${Math.min(score, 100)}%` }}
          />
        </div>
        <div
          className="absolute top-[-4px] h-5 w-[2px] bg-wa-green"
          style={{ left: `${SCORE_CTA_THRESHOLD}%` }}
          title={`WhatsApp CTA (${SCORE_CTA_THRESHOLD})`}
        />
        <div
          className="absolute top-[-4px] h-5 w-[2px] bg-amber-500"
          style={{ left: `${SCORE_ALERT_THRESHOLD}%` }}
          title={`Alerta admisiones (${SCORE_ALERT_THRESHOLD})`}
        />
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-ink-400">
        <span>0</span>
        <span className="text-wa-green">WA {SCORE_CTA_THRESHOLD}</span>
        <span className="text-amber-500">Alerta {SCORE_ALERT_THRESHOLD}</span>
        <span>100</span>
      </div>
    </>
  );
}

function ScoreDeltaRow({ entry }: { entry: ScoreDelta }): React.JSX.Element {
  const sign = entry.delta >= 0 ? "+" : "";
  const color =
    entry.delta > 0
      ? "text-score-hot-fg"
      : entry.delta < 0
        ? "text-score-cold-fg"
        : "text-ink-500";
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className={`font-mono font-semibold ${color}`}>
        {sign}
        {entry.delta}
      </span>
      <span className="text-ink-400">→ score: {entry.after}</span>
    </li>
  );
}
